/**
 * Bottom banner — same bottom bar on mobile and desktop.
 * Settings, help, dashboard, keep learning, and search.
 * Only visible when the learner is signed in.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  ChevronDown,
  Home,
  PanelBottom,
  Settings,
  HelpCircle,
  LayoutDashboard,
  Loader2,
  Target,
  LucideIcon,
} from 'lucide-react';
import { useResponsive } from './MobileResponsive';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';
import { LearnerSearchPanel, LearningSearchScopePayload } from './LearnerSearchPanel';
import { mobilePinnedAudioChromeBottom } from '../../utils/audioBarChromeState';
import { useTourCollapsibleTargetPrefix } from '../../learnerTour/tourTargetEvents';
import { emitLearnerTourOverlayPageKey, useProductTour } from '../../contexts/ProductTourContext';
import { usePageHelpAvailable } from '../../learnerTour/pageHelpAvailability';
import { LEARNER_UI_ANIM_MS } from '../../utils/learnerSlideModalMotion';

interface BottomBannerProps {
  onCollapsedChange?: (collapsed: boolean) => void;
  onDashboardClick?: () => void;
  onOpenSettings?: () => void;
  onHomeClick?: () => void;
  onKeepLearning?: () => void;
  canLimitSearchToLearningContext?: boolean;
  learningSearchScope?: LearningSearchScopePayload;
}

export const BottomBanner: React.FC<BottomBannerProps> = ({
  onCollapsedChange,
  onDashboardClick,
  onOpenSettings,
  onHomeClick,
  onKeepLearning,
  canLimitSearchToLearningContext = false,
  learningSearchScope = {},
}) => {
  const { isMobile } = useResponsive();
  const { user: authUser, isLoading } = useAuth();
  const { t } = useTranslation('common');
  const { t: tTour } = useTranslation('tour');
  const { t: tSearch } = useTranslation('search');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const { isRunning: isTourRunning, basePageKey } = useProductTour();
  const pageHelpAvailable = usePageHelpAvailable(basePageKey);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => authService.isAuthenticated());
  const [bannerCollapsed, setBannerCollapsed] = useState(false);
  const [audioBarPinned, setAudioBarPinned] = useState(false);
  const [audioBarExtraChromePx, setAudioBarExtraChromePx] = useState(0);
  const [floatingIconExiting, setFloatingIconExiting] = useState(false);
  const [tourHelpLoading, setTourHelpLoading] = useState(false);
  const { t: tNav } = useTranslation('nav');

  useTourCollapsibleTargetPrefix('bottom-banner-', !bannerCollapsed, (open) => {
    if (open) {
      setBannerCollapsed(false);
      setFloatingIconExiting(true);
    } else {
      setFloatingIconExiting(false);
      setBannerCollapsed(true);
    }
  });

  useEffect(() => {
    if (showSearchPanel) {
      emitLearnerTourOverlayPageKey('search', true);
    }
  }, [showSearchPanel]);

  const mobileBarRef = useRef<HTMLDivElement>(null);
  const [mobileBarHeightPx, setMobileBarHeightPx] = useState(0);

  const emitBannerState = (heightPx?: number) => {
    const measured =
      heightPx ??
      (!bannerCollapsed && mobileBarRef.current
        ? Math.round(mobileBarRef.current.getBoundingClientRect().height)
        : 0);
    onCollapsedChange?.(bannerCollapsed);
    window.dispatchEvent(
      new CustomEvent('app:bottomBannerState', {
        detail: {
          collapsed: bannerCollapsed,
          isMobile,
          heightPx: bannerCollapsed ? 0 : measured,
        },
      })
    );
  };

  useEffect(() => {
    const respondToRequest = () => emitBannerState();
    emitBannerState();
    window.addEventListener('app:requestBottomBannerState', respondToRequest);
    return () => {
      window.removeEventListener('app:requestBottomBannerState', respondToRequest);
    };
  }, [bannerCollapsed, onCollapsedChange, isMobile, mobileBarHeightPx]);

  useEffect(() => {
    if (bannerCollapsed) {
      setMobileBarHeightPx(0);
      document.documentElement.style.setProperty('--learner-mobile-bottom-banner-height', '0px');
      emitBannerState(0);
      return undefined;
    }
    const el = mobileBarRef.current;
    if (!el) return undefined;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      // Distance from viewport bottom to the banner’s top edge (flush with side nav).
      const h = Math.max(0, Math.round(window.innerHeight - rect.top));
      setMobileBarHeightPx(h);
      document.documentElement.style.setProperty(
        '--learner-mobile-bottom-banner-height',
        `${h}px`
      );
      emitBannerState(h);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === 'transform') measure();
    };
    el.addEventListener('transitionend', onTransitionEnd);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      el.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [isMobile, bannerCollapsed]);

  useEffect(() => {
    const handleAudioBarState = (event: Event) => {
      const customEvent = event as CustomEvent<{
        visible?: boolean;
        pinned?: boolean;
        extraChromeBottomPx?: number;
      }>;
      const visible = customEvent.detail?.visible === true;
      const pinned = visible && customEvent.detail?.pinned === true;
      setAudioBarPinned(pinned);
      setAudioBarExtraChromePx(
        pinned ? (customEvent.detail?.extraChromeBottomPx ?? 0) : 0
      );
    };

    window.addEventListener('app:audioBarState', handleAudioBarState as EventListener);
    return () => {
      window.removeEventListener('app:audioBarState', handleAudioBarState as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!bannerCollapsed && floatingIconExiting) {
      const t = setTimeout(() => setFloatingIconExiting(false), LEARNER_UI_ANIM_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [bannerCollapsed, floatingIconExiting]);

  const handleExpandBanner = () => {
    setBannerCollapsed(false);
    setFloatingIconExiting(true);
  };

  const handleCollapseBanner = () => {
    setFloatingIconExiting(false);
    setBannerCollapsed(true);
  };

  const showFloatingIcon = bannerCollapsed || floatingIconExiting;
  const floatingIconVisible = bannerCollapsed && !floatingIconExiting;
  const [floatingIconReady, setFloatingIconReady] = useState(false);
  useEffect(() => {
    if (showFloatingIcon) {
      setFloatingIconReady(false);
      const id = requestAnimationFrame(() => setFloatingIconReady(true));
      return () => cancelAnimationFrame(id);
    }
    setFloatingIconReady(false);
    return undefined;
  }, [showFloatingIcon]);
  const floatingIconOpacity = floatingIconVisible && floatingIconReady;

  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(authService.isAuthenticated());
    };
    checkAuth();
    const handleAuthChange = () => checkAuth();
    const interval = setInterval(checkAuth, 200);
    window.addEventListener('auth:login', handleAuthChange);
    window.addEventListener('auth:logout', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('auth:login', handleAuthChange);
      window.removeEventListener('auth:logout', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, []);

  if (!isAuthenticated && (!authUser || isLoading)) {
    return null;
  }

  const requestPageTour = () => {
    if (!pageHelpAvailable || tourHelpLoading || isTourRunning) return;
    setTourHelpLoading(true);
    // Let lesson pages collapse journey (and similar prep) before the tour resolves its first target.
    window.dispatchEvent(new CustomEvent('app:beforeRequestPageTour'));
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('app:requestPageTour'));
    }, 320);
  };

  useEffect(() => {
    if (isTourRunning && tourHelpLoading) {
      setTourHelpLoading(false);
    }
  }, [isTourRunning, tourHelpLoading]);

  useEffect(() => {
    if (!tourHelpLoading) return undefined;
    const timer = window.setTimeout(() => setTourHelpLoading(false), 8000);
    return () => window.clearTimeout(timer);
  }, [tourHelpLoading]);

  const setSearchPanelOpen = (open: boolean) => {
    setShowSearchPanel(open);
    if (open) {
      emitLearnerTourOverlayPageKey('search', true);
    } else {
      emitLearnerTourOverlayPageKey(null, false);
    }
  };

  const toggleSearchPanel = () => {
    setShowSearchPanel((open) => {
      const next = !open;
      if (next) emitLearnerTourOverlayPageKey('search', true);
      else emitLearnerTourOverlayPageKey(null, false);
      return next;
    });
  };

  const mobileActions = (
    <>
      <BannerAction
        icon={Settings}
        label={t('settings')}
        onClick={() => onOpenSettings?.()}
        title={t('settings')}
        compact
        dataTourId="bottom-banner-settings"
      />
      <BannerAction
        icon={HelpCircle}
        label={t('help')}
        onClick={requestPageTour}
        title={
          pageHelpAvailable
            ? tTour('pageHelp')
            : tTour('pageHelpUnavailable', {
                defaultValue: isRtl ? 'برای این صفحه راهنمایی نیست' : 'No guide on this page',
              })
        }
        compact
        loading={tourHelpLoading}
        unavailable={!pageHelpAvailable}
        dataTourId="bottom-banner-help"
      />
      {onDashboardClick && <BannerDashboardAction onClick={() => onDashboardClick()} />}
      <BannerAction
        icon={Target}
        label={t('keepLearning')}
        onClick={() => onKeepLearning?.()}
        title={t('keepLearning')}
        compact
        dataTourId="bottom-banner-keep-learning"
      />
      <BannerAction
        icon={Search}
        label={t('search')}
        onClick={toggleSearchPanel}
        title={showSearchPanel ? tSearch('closeAriaLabel') : t('searchCoursesAndLessons')}
        compact
        dataTourId="bottom-banner-search"
      />
    </>
  );

  return (
      <>
        {showFloatingIcon && (
          <button
            type="button"
            onClick={handleExpandBanner}
            className={`edge-fab show-tools-toggle fixed z-[65] flex items-center justify-center rounded-full shadow-lg ${
              isMobile ? 'h-9 w-9' : 'h-11 w-11'
            } ${isRtl ? 'right-4' : 'left-4'} ${
              floatingIconOpacity ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
            }`}
            style={{
              bottom: audioBarPinned
                ? mobilePinnedAudioChromeBottom(true, audioBarExtraChromePx)
                : '1rem',
            }}
            aria-label={t('showTools')}
            title={t('showTools')}
            tabIndex={floatingIconVisible ? 0 : -1}
          >
            <PanelBottom className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} shrink-0`} aria-hidden />
          </button>
        )}

        <div
          ref={mobileBarRef}
          className={`learner-bottom-banner fixed left-0 right-0 z-[60] overflow-visible transition-transform duration-200 ease-out ${
            bannerCollapsed
              ? 'pointer-events-none bottom-0 translate-y-full'
              : 'bottom-0 translate-y-0'
          }`}
        >
          <div
            className="learner-chrome-glass pointer-events-none absolute inset-0 border-t border-gray-200 dark:border-gray-700"
            aria-hidden
          />
          <div className="relative z-10 w-full overflow-visible py-1.5">
            <div className="flex w-full items-end overflow-visible">
              <div className="flex flex-shrink-0 items-center pl-1">
                <button
                  type="button"
                  data-tour-id="bottom-banner-collapse"
                  onClick={handleCollapseBanner}
                  className="flex min-h-10 min-w-9 flex-col items-center justify-center rounded-lg px-0.5 py-1 transition-all hover:bg-gray-200/50 dark:hover:bg-gray-700/50"
                  aria-label={t('hideTools')}
                  title={t('hideTools')}
                >
                  <ChevronDown className="learner-chrome-icon h-5 w-5 dark:text-gray-300" />
                </button>
                <div className="mx-0.5 h-6 w-px flex-shrink-0 bg-gray-300 dark:bg-gray-500" aria-hidden />
              </div>
              <div className="flex min-w-0 flex-1 items-end justify-center overflow-visible">
                <div className={`flex flex-shrink-0 items-end overflow-visible ${isMobile ? 'gap-1.5' : 'gap-6'}`}>{mobileActions}</div>
              </div>
              {onHomeClick && (
                <div className="flex flex-shrink-0 items-center pr-1">
                  <div className="mx-0.5 h-6 w-px flex-shrink-0 bg-gray-300 dark:bg-gray-500" aria-hidden />
                  <BannerHomeAction onClick={() => onHomeClick()} compact />
                </div>
              )}
            </div>
          </div>
        </div>

        <LearnerSearchPanel
          isOpen={showSearchPanel}
          onClose={() => setSearchPanelOpen(false)}
          isMobile={isMobile}
          audioBarPinned={audioBarPinned}
          audioBarExtraChromePx={audioBarExtraChromePx}
          bannerCollapsed={bannerCollapsed}
          canLimitToLearningContext={canLimitSearchToLearningContext}
          learningScope={learningSearchScope}
        />
      </>
    );
};

function BannerAction({
  icon: Icon,
  label,
  onClick,
  title,
  compact = false,
  loading = false,
  unavailable = false,
  badge = 0,
  dataTourId,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  title?: string;
  compact?: boolean;
  loading?: boolean;
  unavailable?: boolean;
  badge?: number;
  dataTourId?: string;
}) {
  const blocked = loading || unavailable;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={blocked}
      aria-disabled={unavailable || undefined}
      {...(dataTourId ? { 'data-tour-id': dataTourId } : {})}
      className={`flex flex-col items-center justify-center rounded-lg transition-all hover:bg-gray-200/50 active:scale-100 dark:hover:bg-gray-700/50 ${
        unavailable ? 'cursor-not-allowed opacity-40' : 'hover:scale-105'
      } ${loading ? 'pointer-events-none opacity-80' : ''} ${
        compact ? 'min-h-10 min-w-[2.65rem] px-0.5 py-1' : 'min-h-11 min-w-[3.25rem] px-2 py-1'
      }`}
      title={title ?? label}
      aria-label={label}
      aria-busy={loading}
    >
      <span className="relative">
        {loading ? (
          <Loader2 className="learner-chrome-icon h-5 w-5 shrink-0 animate-spin dark:text-gray-300" aria-hidden />
        ) : (
          <Icon className="learner-chrome-icon h-5 w-5 shrink-0 dark:text-gray-300" />
        )}
        {badge > 0 && (
          <span className="absolute -end-2 -top-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </span>
      <span
        className={`learner-chrome-readable max-w-[4.5rem] text-center text-[9px] leading-tight dark:text-gray-300 sm:max-w-none sm:text-xs ${
          compact ? 'mt-0 max-w-[3.25rem] truncate' : 'mt-0.5'
        }`}
      >
        {label}
      </span>
    </button>
  );
}

function BannerHomeAction({ onClick, compact = false }: { onClick: () => void; compact?: boolean }) {
  const { t } = useTranslation('common');
  return (
    <button
      type="button"
      data-tour-id="bottom-banner-home"
      onClick={onClick}
      className={`flex flex-col items-center justify-center rounded-lg transition-all hover:bg-gray-200/50 dark:hover:bg-gray-700/50 ${
        compact ? 'min-h-10 min-w-9 px-0.5 py-1' : 'min-h-11 min-w-10 px-2 py-1'
      }`}
      aria-label={t('goToHome')}
      title={t('goToHome')}
    >
      <Home className="learner-chrome-icon h-5 w-5 dark:text-gray-300" />
      {!compact && (
        <span className="learner-chrome-readable mt-0.5 hidden text-[10px] leading-tight dark:text-gray-300 sm:block">
          {t('home')}
        </span>
      )}
    </button>
  );
}

function BannerDashboardAction({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('common');
  const { t: tNav } = useTranslation('nav');
  return (
    <button
      type="button"
      data-tour-id="bottom-banner-dashboard"
      onClick={onClick}
      className="relative z-10 -mt-2.5 flex min-h-11 min-w-11 shrink-0 flex-col items-center justify-center rounded-full border-2 border-white/40 bg-white/25 p-3 shadow-lg backdrop-blur-md transition-all hover:scale-110 active:scale-100 dark:border-white/30 dark:bg-white/15"
      title={tNav('learningDashboard')}
      aria-label={t('openLearningDashboard')}
    >
      <LayoutDashboard className="h-7 w-7 text-blue-600 dark:text-blue-400" />
    </button>
  );
}

